import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useI18n } from '@/hooks/useI18n';

interface NotificationData {
  notice_type: 'normal' | 'deviation' | 'deviation_warning' | 'deviation_severe' | 'deviation_final' | 'poor_response' | 'excellent_response';
  notice_title: string;
  notice_content: string;
}

interface NotificationBarProps {
  notification?: NotificationData;
  visible?: boolean;
  onClose?: () => void;
}

const NotificationBar: React.FC<NotificationBarProps> = ({
  notification,
  visible = false,
  onClose
}) => {
  const { t } = useI18n();
  // 默认显示状态，当没有通知时显示默认消息
  const hasNotification = notification && visible;
  const displayNotification = hasNotification ? notification : {
    notice_type: 'normal' as const,
    notice_title: t('ai.chat.noMessages', '暂无消息'),
    notice_content: t('ai.chat.noNewNotifications', '当前没有新的通知消息')
  };

  const getNotificationConfig = (type: string) => {
    switch (type) {
      case 'excellent_response':
        return {
          bgGradient: 'from-green-100 to-emerald-50',
          textColor: 'text-green-800',
          icon: <CheckCircleOutlined className="text-green-600 text-lg" />,
          borderColor: 'border-green-200'
        };
      case 'deviation':
        return {
          bgGradient: 'from-yellow-100 to-orange-50',
          textColor: 'text-yellow-800',
          icon: <ExclamationCircleOutlined className="text-yellow-600 text-lg" />,
          borderColor: 'border-yellow-200'
        };
      case 'deviation_warning':
        return {
          bgGradient: 'from-orange-100 to-red-50',
          textColor: 'text-orange-800',
          icon: <ExclamationCircleOutlined className="text-orange-600 text-lg" />,
          borderColor: 'border-orange-200'
        };
      case 'deviation_severe':
        return {
          bgGradient: 'from-red-100 to-red-200',
          textColor: 'text-red-800',
          icon: <CloseCircleOutlined className="text-red-600 text-lg" />,
          borderColor: 'border-red-300'
        };
      case 'deviation_final':
        return {
          bgGradient: 'from-red-200 to-red-300',
          textColor: 'text-red-900',
          icon: <CloseCircleOutlined className="text-red-700 text-xl" />,
          borderColor: 'border-red-400'
        };
      case 'poor_response':
        return {
          bgGradient: 'from-orange-100 to-yellow-50',
          textColor: 'text-orange-800',
          icon: <ExclamationCircleOutlined className="text-orange-600 text-lg" />,
          borderColor: 'border-orange-200'
        };
      case 'normal':
      default:
        return {
          bgGradient: 'from-blue-100 to-indigo-50',
          textColor: 'text-blue-800',
          icon: <InfoCircleOutlined className="text-blue-600 text-lg" />,
          borderColor: 'border-blue-200'
        };
    }
  };

  const config = getNotificationConfig(displayNotification.notice_type);

  return (
    <motion.div
      className={`bg-gradient-to-r ${config.bgGradient} mx-4 my-4 rounded-2xl border flex-1 ${config.borderColor}`}
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.5,
        type: "spring",
        stiffness: 300,
        damping: 30
      }}
    >
      <motion.div
        className="px-4 py-4 rounded-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="flex items-start justify-between">
          <motion.div
            className="flex items-start flex-1"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <div className="mr-3 mt-0.5">
              {config.icon}
            </div>
            <div className="flex-1">
              <motion.div
                className={`${config.textColor} text-base leading-6 font-medium mb-1`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                {displayNotification.notice_title}
              </motion.div>
              <motion.div
                className={`${config.textColor} text-sm leading-5 opacity-90`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                {displayNotification.notice_content}
              </motion.div>
            </div>
          </motion.div>
          {onClose && hasNotification && (
            <motion.button
              onClick={onClose}
              className={`${config.textColor} opacity-60 hover:opacity-100 ml-2 p-1 rounded-full transition-opacity duration-200`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.6, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationBar;